/* eslint-disable @typescript-eslint/no-empty-object-type */
import { Constructor, WithExcludedUndefinedFields } from '@deltasierra/type-utilities';
import { getKeys } from '@deltasierra/utilities-object';
import { Omit } from './types';

/**
 * Derived from https://developer.mozilla.org/en/docs/Web/JavaScript/Reference/Global_Objects/Object/freeze
 *
 * @param objects - The objects to freeze
 * @returns The objects frozen
 */
export function deepFreeze<T>(objects: T[]): ReadonlyArray<T>;
export function deepFreeze<T>(obj: T): Readonly<T> {
    const propNames = Object.getOwnPropertyNames(obj);
    propNames.forEach(name => {
        const prop = (obj as Untyped)[name];
        if (typeof prop === 'object' && prop !== null) {
            deepFreeze(prop);
        }
    });
    return Object.freeze(obj);
}

export function singleton<T>(create: () => T): () => T {
    let instance: T;

    return () => {
        if (instance === undefined) {
            instance = create();
        }
        return instance;
    };
}

/**
 * A simpler, no fuss version of singletons.
 *
 * @example
 * class Foo {
 *     public static readonly instance = proxySingleton(() => new Foo());
 *     public bar() { ... }
 * }
 * Foo.instance.bar();
 * @param create - The factory function to create an instance of the singleton
 * @returns A proxy-wrapped-instance of the singleton
 */
export function proxySingleton<T>(create: () => T): T {
    const globalOrWindow: { [key: string]: any } = typeof global !== 'undefined' ? global : window;
    const ProxyConstructor: Constructor<any> | undefined = globalOrWindow.Proxy;
    const singletonCreator = singleton(create);

    if (typeof ProxyConstructor === 'undefined') {
        throw new Error('Can only use proxySingleton if Proxies are enabled');
    }

    return new ProxyConstructor(
        {},
        {
            get(_: any, key: keyof T) {
                const instance = singletonCreator();
                return instance[key];
            },
        },
    );
}

/**
 * Use this to mark a type as something requiring type information. Usually used when working out the actual type is
 * too hard.
 */
export type Untyped = any;

export function replaceEmptyStringsWithNulls<T extends object>(obj: T): void {
    const innerReplaceEmptyStringsWithNulls = (current: object, previous: object[]) => {
        if (previous.includes(current)) {
            return;
        }

        for (const key of Object.getOwnPropertyNames(current)) {
            const value = (current as Untyped)[key];

            if (value === '') {
                (current as Untyped)[key] = null;
            } else if (value && typeof value === 'object') {
                innerReplaceEmptyStringsWithNulls(value, []);
            }
        }
    };

    innerReplaceEmptyStringsWithNulls(obj, []);
}

/**
 * Pick key from an object.
 *
 * @example
 * pick({ name: 'Frank' }, 'name') // { name: 'Frank' }
 * pick({ name: 'Frank' }, 'name', 'age') // { name: 'Frank' }
 * @param obj - The object to pick the keys from
 * @param keys - The keys to pick from the object
 * @returns All the keys picked from the object (missing keys are skipped)
 */
export function pick<TObject, TKeys extends ReadonlyArray<keyof TObject>>(
    obj: TObject,
    ...keys: TKeys
): Pick<TObject, TKeys[number]>;
/**
 * Pick keys from an object and default missing keys to undefined.
 *
 * @deprecated Argument order isn't hard to use and default to undefined is confusing and not clear
 * @example
 * pick(['name'], { name: 'Frank' }) // { name: 'Frank' }
 * pick(['name', 'age'], { name: 'Frank' }) // { name: 'Frank', age: undefined }
 * @param keys - The keys to pick from the object
 * @param obj - The object to pick from
 * @returns All the keys picked from the object. If a key is not found it will be set to undefined
 */
export function pick<TObject, TKeys extends ReadonlyArray<keyof TObject>>(
    keys: TKeys,
    obj: TObject,
): Pick<TObject, TKeys[number]>;
export function pick<TObject, TKeys extends ReadonlyArray<keyof TObject>>(
    ...args: [TKeys, TObject] | [TObject, ...TKeys]
): Pick<TObject, TKeys[number]> {
    const isDeprecatedArgs = (val: [TKeys, TObject] | [TObject, ...TKeys]): val is [TKeys, TObject] =>
        Array.isArray(val[0]);
    if (isDeprecatedArgs(args)) {
        // Old incorrect behavior
        const [keys, obj] = args;
        const result: any = {};
        for (const key of keys) {
            result[key] = obj[key];
        }
        return result;
    } else {
        // New and improved logic
        const [obj, ...keys] = args;
        const result: any = {};
        for (const key of keys) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                result[key] = obj[key];
            }
        }
        return result;
    }
}

/**
 * Picks the keys from the object into a new object, excluding undefined property values (will not exclude null values)
 *
 * @param obj - The object
 * @param keys - The keys
 * @returns - The object without undefined values
 */
export function pickAndExludeUndefined<TObject, TKeys extends ReadonlyArray<keyof TObject>>(
    obj: TObject,
    ...keys: TKeys
): WithExcludedUndefinedFields<Pick<TObject, TKeys[number]>> {
    const safeObject = pick(obj, ...keys);
    return excludeUndefined(safeObject) as unknown as WithExcludedUndefinedFields<Pick<TObject, TKeys[number]>>;
}

/**
 * Force pick keys from an object. Does not check if the property exists.
 *
 * @example
 * pick({ name: 'Frank' }, 'name') // { name: 'Frank' }
 * pick({ name: 'Frank' }, 'name', 'age') // { name: 'Frank' }
 * @param obj - The object to pick the keys from
 * @param keys - The keys to pick from the object
 * @returns All the keys picked from the object
 */
export function pickForce<TObject, TKeys extends ReadonlyArray<keyof TObject>>(
    obj: TObject,
    ...keys: TKeys
): Pick<TObject, TKeys[number]> {
    const result: any = {};
    for (const key of keys) {
        result[key] = obj[key];
    }
    return result;
}

/**
 * Exclude any undefined values in the object
 *
 * @param obj - The object
 * @returns - The object excluding undefined values
 */
export function excludeUndefined<TObject extends Record<string, unknown>>(
    obj: TObject,
): WithExcludedUndefinedFields<TObject> {
    // New and improved logic
    const result: any = {};
    for (const key of getKeys(obj)) {
        if (typeof obj[key] !== 'undefined' && obj[key] !== undefined) {
            result[key] = obj[key];
        }
    }
    return result;
}

/**
 * Omit keys from an object.
 *
 * @example
 * omit({ name: 'Frank', age: '12' }, 'age') // { name: 'Frank' }
 * omit({ name: 'Frank', age: '12' }, 'name', 'age') // { }
 * @param obj - The object to omit the keys from
 * @param keys - The keys to omit from the object
 * @returns All the keys omitted from the object (missing keys are skipped)
 */
export function omit<TObject, TKeys extends ReadonlyArray<keyof TObject>>(
    obj: TObject,
    ...keys: TKeys
): Omit<TObject, TKeys[number]>;
/**
 * Pick keys from an object and default missing keys to undefined.
 *
 * @deprecated Argument order isn't hard to use and default to undefined is confusing and not clear
 * @example
 * omit(['age'], { name: 'Frank', age: '12' }) // { name: 'Frank' }
 * omit(['name', 'age'], { name: 'Frank', age: '12' }) // { }
 * @param keys - The keys to pick from the object
 * @param obj - The object to pick from
 * @returns All the keys picked from the object. If a key is not found it will be set to undefined
 */
export function omit<TObject extends {}, TKeys extends ReadonlyArray<keyof TObject>>(
    keys: TKeys,
    obj: TObject,
): Omit<TObject, TKeys[number]>;
export function omit<TObject extends {}, TKeys extends ReadonlyArray<keyof TObject>>(
    ...args: [TKeys, TObject] | [TObject, ...TKeys]
): Omit<TObject, TKeys[number]> {
    const isDeprecatedArgs = (val: [TKeys, TObject] | [TObject, ...TKeys]): val is [TKeys, TObject] =>
        Array.isArray(val[0]);
    if (isDeprecatedArgs(args)) {
        // Old incorrect behavior
        const [keys, obj] = args;
        const result: any = {};
        for (const key of Object.keys(obj)) {
            if (!keys.includes(key as keyof TObject)) {
                result[key] = obj[key as keyof TObject];
            }
        }
        return result;
    } else {
        // New and improved logic
        const [obj, ...keys] = args;
        return pick(
            obj,
            ...(Object.keys(obj).filter(key => !keys.includes(key as keyof TObject)) as Array<keyof TObject>),
        );
    }
}

export function hasOwnProperty<T extends object>(obj: T | null | undefined, propertyName: string | keyof T): boolean {
    return Object.prototype.hasOwnProperty.call(obj, propertyName);
}

export function isNullOrUndefined(val: any): val is null | undefined {
    return val === null || typeof val === 'undefined';
}

export function isNotNullOrUndefined<T>(val: T | null | undefined): val is T {
    return !isNullOrUndefined(val);
}

export function isDefined<T>(val: T | null | undefined): val is T {
    return isNotNullOrUndefined(val);
}

export function isPropertyNotNullOrUndefined<
    T,
    Key extends keyof T,
    R extends T & {
        [key in Key]-?: Exclude<T[key], null | undefined>;
    },
>(val: T | null | undefined, key: Key): val is R {
    return isNotNullOrUndefined(val) && isNotNullOrUndefined(val[key]);
}

export function isPropertyNotNullOrUndefinedFilter<
    T,
    Key extends keyof T,
    R extends T & {
        [key in Key]-?: Exclude<T[key], null | undefined>;
    },
>(key: Key): (val: T | null) => val is R {
    return (val: T | null): val is R => isPropertyNotNullOrUndefined(val, key);
}

/**
 * Check whether a value is a record type (or something that looks like it). This is really helpful when writing
 * user-defined type guards for complex types.
 *
 * "Why does this function use `keyof never`?"
 * This returns a union of all possible key types and means you don't have to hardcode that list anywhere.
 *
 * @example
 * type User = {
 *     userId: number;
 * }
 * function isUser(value: unknown) {
 *     return isRecord(value) && typeof value.userId === 'number';
 * }
 * @param value - The value to check
 * @returns Whether or not the arguments looks like a record type
 * @see {@link https://github.com/microsoft/TypeScript/issues/33025#issuecomment-524053918 keyof never}
 */
export function isRecordType(value: unknown): value is Record<keyof never, unknown> {
    return typeof value === 'object' && !!value;
}
